import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/services/api";
import {
  Users,
  Gift,
  TrendingUp,
  Share2,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Instagram,
  Phone,
  Mail,
  Lock,
  User,
  ChevronRight,
} from "lucide-react";

const BENEFITS = [
  {
    icon: Share2,
    title: "Comparte tu link",
    description: "Obtén tu enlace único y compártelo en redes sociales"
  },
  {
    icon: Gift,
    title: "Gana comisiones",
    description: "Recibe dinero por cada compra que generen tus referidos"
  },
  {
    icon: TrendingUp,
    title: "Crece con nosotros",
    description: "Desbloquea niveles y aumenta tus ganancias"
  },
];

const COMO_CONOCISTE_OPTIONS = [
  "Instagram",
  "Facebook",
  "TikTok",
  "WhatsApp",
  "Un amigo/familiar",
  "Google",
  "Otro"
];

export default function EmbajadorRegistro() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [codigoGenerado, setCodigoGenerado] = useState("");
  
  const [form, setForm] = useState({
    nombre: "",
    email: "",
    telefono: "",
    password: "",
    confirmPassword: "",
    instagram: "",
    como_conociste: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.nombre || !form.email || !form.telefono || !form.password) {
      toast.error("Por favor completa todos los campos obligatorios");
      return;
    }
    
    if (form.password !== form.confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    
    if (form.password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post("/embajadores/registro", {
        nombre: form.nombre,
        email: form.email,
        telefono: form.telefono,
        password: form.password,
        instagram: form.instagram || undefined,
        como_conociste: form.como_conociste || undefined,
      });
      
      setCodigoGenerado(response.data.codigo_afiliado);
      setSuccess(true);
      toast.success("¡Solicitud enviada exitosamente!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrar");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-3xl shadow-xl p-8 border border-amber-100">
            <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <CheckCircle2 className="w-10 h-10 text-white" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              ¡Solicitud Enviada!
            </h1>
            <p className="text-gray-600 mb-6">
              Tu solicitud para ser embajador está siendo revisada. Te notificaremos cuando sea aprobada.
            </p>
            
            <div className="bg-amber-50 rounded-2xl p-4 mb-6">
              <p className="text-sm text-amber-800 mb-2">Tu link de embajador será:</p>
              <p className="font-mono text-amber-900 font-semibold">
                barujshefa.com/s/{codigoGenerado}
              </p>
            </div>
            
            <div className="space-y-3">
              <Button
                onClick={() => navigate("/embajador/login")}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-6 rounded-xl"
              >
                Ir al Login
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                onClick={() => navigate("/")}
                variant="ghost"
                className="w-full"
              >
                Volver al inicio
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-orange-500/10"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-300/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange-300/20 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-6xl mx-auto px-4 py-12 lg:py-20">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Programa de Embajadores
            </div>
            
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Conviértete en{" "}
              <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
                Embajador
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Gana dinero compartiendo los productos que amas de Baruj Shefa. 
              Sin inventario, sin complicaciones.
            </p>
          </div>
          
          {/* Benefits */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {BENEFITS.map((benefit, index) => (
              <div 
                key={index}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-amber-100 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Registration Form */}
      <div className="max-w-md mx-auto px-4 pb-20">
        <div className="bg-white rounded-3xl shadow-xl p-8 border border-amber-100">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Regístrate ahora</h2>
            <p className="text-gray-600 text-sm mt-2">
              Completa tus datos y empieza a ganar
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre completo *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  placeholder="Tu nombre"
                  className="pl-10 h-12 rounded-xl border-gray-200 focus:border-amber-500 focus:ring-amber-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="tu@email.com"
                  className="pl-10 h-12 rounded-xl border-gray-200 focus:border-amber-500 focus:ring-amber-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                WhatsApp / Teléfono *
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="tel"
                  value={form.telefono}
                  onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                  placeholder="300 123 4567"
                  className="pl-10 h-12 rounded-xl border-gray-200 focus:border-amber-500 focus:ring-amber-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instagram (opcional)
              </label>
              <div className="relative">
                <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  value={form.instagram}
                  onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                  placeholder="@tu_usuario"
                  className="pl-10 h-12 rounded-xl border-gray-200 focus:border-amber-500 focus:ring-amber-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ¿Cómo nos conociste?
              </label>
              <Select
                value={form.como_conociste}
                onValueChange={(value) => setForm({ ...form, como_conociste: value })}
              >
                <SelectTrigger className="h-12 rounded-xl border-gray-200">
                  <SelectValue placeholder="Selecciona una opción" />
                </SelectTrigger>
                <SelectContent>
                  {COMO_CONOCISTE_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contraseña *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Mínimo 6 caracteres"
                  className="pl-10 h-12 rounded-xl border-gray-200 focus:border-amber-500 focus:ring-amber-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirmar contraseña *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="password"
                  value={form.confirmPassword}
                  onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                  placeholder="Repite tu contraseña"
                  className="pl-10 h-12 rounded-xl border-gray-200 focus:border-amber-500 focus:ring-amber-500"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold text-lg rounded-xl mt-6 shadow-lg shadow-amber-500/20"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Registrando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Quiero ser Embajador
                  <ChevronRight className="w-5 h-5" />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600 text-sm">
              ¿Ya tienes cuenta?{" "}
              <Link 
                to="/embajador/login" 
                className="text-amber-600 hover:text-amber-700 font-medium"
              >
                Inicia sesión
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
